import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AUTO_REDIRECT_DOMAIN = ''; // Disabled - was 'client.kioskservice.club'
const IDLE_TIMEOUT = 60000; // 60 seconds

/**
 * Hook to automatically redirect to home page after 60 seconds of inactivity
 * Only active when domain is client.kioskservice.club
 */
export function useAutoRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(false);

  useEffect(() => {
    // Check if current domain matches the auto-redirect domain
    const currentHost = window.location.hostname;
    const shouldActivate = currentHost === AUTO_REDIRECT_DOMAIN;

    if (!shouldActivate) {
      // Clean up if domain doesn't match
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isActiveRef.current = false;
      return;
    }

    // Don't redirect if already on home page
    if (location.pathname === '/') {
      isActiveRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    isActiveRef.current = true;

    // Function to reset the timer
    const resetTimer = () => {
      if (!isActiveRef.current) return;
      
      // Don't set timer if already on home page
      if (location.pathname === '/') {
        isActiveRef.current = false;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return;
      }

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout to redirect to home
      timeoutRef.current = setTimeout(() => {
        if (isActiveRef.current && location.pathname !== '/') {
          navigate('/', { replace: true });
        }
      }, IDLE_TIMEOUT);
    };

    // Function to handle user activity
    const handleActivity = () => {
      if (isActiveRef.current) {
        resetTimer();
      }
    };

    // Initial timer setup
    resetTimer();

    // Listen to various user interaction events
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'keypress',
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      isActiveRef.current = false;
    };
  }, [navigate, location.pathname]);
}

